import { ApiError, codeWords, type ContractTerm, type ContractTermSet } from '../../lib/api'

// The words for a vendor's contract readings, and the small rules the
// documents panel and the head chip share (release 3.3, documents lane D4 and
// D5). One copy, so the chip and the panel never call the same thing two names.

// A kind of personal data, as a lead reads it.
export const KIND_WORDS: Record<string, string> = {
  AADHAAR: 'Aadhaar',
  PAN: 'PAN',
  MOBILE: 'Mobile',
  EMAIL: 'E-mail',
  CARD: 'Card',
  BANK_ACCOUNT: 'Bank account',
  NAME: 'Name',
  ADDRESS: 'Address',
  DOB: 'Date of birth',
  MINOR_DOB: "A child's date of birth",
  GSTIN: 'GSTIN',
  TAN: 'TAN',
  PASSPORT: 'Passport',
  VOTER_ID: 'Voter ID',
  UPI: 'UPI ID',
}

// What each kind of contract reading is, as a lead reads it.
export const TERM_WORDS: Record<string, string> = {
  CATEGORY: 'Data',
  SUBJECT: 'Whose data',
  PURPOSE: 'Purpose',
  TRANSFER: 'Country',
  LEAVES_INDIA: 'Leaves India',
  SAFEGUARD: 'Transfer safeguard',
  SUBPROCESSOR: 'Sub-processor',
  SUBPROCESSING: 'New sub-processors',
  RETENTION: 'Kept for',
  UNTIL_TERMINATION: 'Kept for',
  RETURN_DELETE: 'Return or deletion',
  BREACH_NOTICE: 'Breach notice',
}

export function kindWord(code: string): string {
  return KIND_WORDS[code] ?? codeWords(code)
}

// A country by its two letter code, in words where the runtime knows it.
export function countryWord(code: string): string {
  if (code === 'IN') return 'India'
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) ?? code
  } catch {
    return code
  }
}

// The value a person stood behind: the corrected one where they corrected it.
export function heldValue(t: ContractTerm): string {
  return t.decision?.value || t.value || ''
}

export function termValue(t: ContractTerm): string {
  const v = heldValue(t)
  switch (t.kind) {
    case 'CATEGORY':
      return kindWord(t.code)
    case 'TRANSFER':
      return countryWord(t.code)
    case 'LEAVES_INDIA':
      return v === 'true' ? 'Yes' : 'No, stays in India'
    case 'SAFEGUARD':
      // The safeguard's own words are a quotation, withheld from a reader
      // who may not read the restricted document it is in.
      return t.withheld ? 'Named in the contract' : t.matched
    case 'SUBPROCESSOR':
      return [v, t.detail?.country].filter(Boolean).join(', ')
    case 'SUBPROCESSING':
      return t.code === 'PRIOR_CONSENT'
        ? 'Only with prior consent'
        : t.code === 'PRIOR_NOTICE'
          ? 'Prior notice, may object'
          : "At the vendor's discretion"
    case 'RETENTION':
      return v + ' months'
    case 'UNTIL_TERMINATION':
      return 'Until the agreement ends'
    case 'RETURN_DELETE':
      return v ? 'Within ' + v + ' days of the end' : 'At the end, no deadline stated'
    case 'BREACH_NOTICE':
      return t.code === 'WITHIN_HOURS' ? 'Within ' + v + ' hours' : codeWords(t.code)
  }
  return codeWords(t.code)
}

// Confidence is a sort key, never a verdict, so it is shown as a word.
export const HIGH_MATCH = 0.85

export function matchWord(c: number): ['ok' | 'neutral' | 'warn', string] {
  if (c >= HIGH_MATCH) return ['ok', 'High']
  if (c >= 0.65) return ['neutral', 'Medium']
  return ['warn', 'Low']
}

// The readings whose value a person can correct, and what the value counts.
export const EDIT_UNITS: Record<string, string> = {
  RETENTION: 'months',
  RETURN_DELETE: 'days after the end',
  BREACH_NOTICE: 'hours',
  SUBPROCESSOR: 'legal name',
}

export function editable(t: ContractTerm): boolean {
  if (t.kind === 'BREACH_NOTICE') return t.code === 'WITHIN_HOURS'
  return t.kind in EDIT_UNITS
}

// Every reading waiting for a decision that the panel shows as a High match.
export function highReadings(set: ContractTermSet): ContractTerm[] {
  return set.proposals.filter((t) => t.status === 'PROPOSED' && t.confidence >= HIGH_MATCH && t.locator !== '')
}

export type Decision = { key: string; decision: 'ACCEPT' | 'REJECT'; value?: string; note?: string }

// One request per document, each naming the reading of that document the
// person looked at.
export type DecideBatch = { documentId: string; runId: string; decisions: Decision[] }

export function batchesFor(set: ContractTermSet, items: { term: ContractTerm; decision: Decision }[]): DecideBatch[] {
  const out: DecideBatch[] = []
  for (const { term, decision } of items) {
    let b = out.find((x) => x.documentId === term.documentId)
    if (!b) {
      const runId = set.documents.find((d) => d.documentId === term.documentId)?.runId ?? ''
      b = { documentId: term.documentId, runId, decisions: [] }
      out.push(b)
    }
    b.decisions.push(decision)
  }
  return out
}

// What a refused decision asks of the person: a subject count band to start
// the record, or a look at the document's new reading.
export function refusalAsks(err: unknown): 'band' | 'readAgain' | null {
  if (!(err instanceof ApiError)) return null
  if (err.status === 409 && err.code === 'READ_AGAIN') return 'readAgain'
  if (err.status === 422 && err.issues.some((i) => i.field === 'subjectCountBand')) return 'band'
  return null
}

export const READ_AGAIN_WORDS = 'This document was read again; decide on the new reading.'

// The five bands, in order, for when the catalogue cannot be read.
export const BAND_CODES = ['NONE', 'UNDER_1K', 'UNDER_100K', 'UNDER_1M', 'OVER_1M']

// The two sides of a drift gap (G7), from its sentence: what the contract
// names that the record does not, and what the record has that the contract
// does not name.
export function driftSides(detail: string): { contract: string[]; record: string[] } {
  const list = (m: RegExpExecArray | null) =>
    (m?.[1] ?? '')
      .split(', ')
      .filter((c) => c.trim() !== '')
      .map((c) => kindWord(c.trim()))
  return {
    contract: list(/the contract names (.+?), which the data record does not/.exec(detail)),
    record: list(/the data record has (.+?), which the contract does not name/.exec(detail)),
  }
}

// The vendor head's one line: what the accepted readings say, or how many
// wait for a decision, or nothing when nothing was read.
export function chipWords(set: ContractTermSet | null): string | null {
  if (!set) return null
  const accepted = set.proposals.filter((t) => t.status === 'ACCEPT')
  const parts: string[] = []
  const once = (list: string[]) => list.filter((x, i) => list.indexOf(x) === i)

  const data = once(accepted.filter((t) => t.kind === 'CATEGORY').map((t) => kindWord(t.code)))
  const whose = once(accepted.filter((t) => t.kind === 'SUBJECT').map((t) => codeWords(t.code).toLowerCase()))
  if (data.length > 0) parts.push(data.join(', ') + (whose.length > 0 ? ' of ' + whose.join(', ') : ''))

  const abroad = once(accepted.filter((t) => t.kind === 'TRANSFER' && t.code !== 'IN').map((t) => countryWord(t.code)))
  const leaves = accepted.some((t) => t.kind === 'LEAVES_INDIA' && heldValue(t) === 'true')
  if (abroad.length > 0) parts.push('leaves India (' + abroad.join(', ') + ')')
  else if (leaves) parts.push('leaves India')

  const subs = once(accepted.filter((t) => t.kind === 'SUBPROCESSOR').map((t) => heldValue(t).toLowerCase()))
  if (subs.length > 0) parts.push(subs.length + (subs.length === 1 ? ' sub-processor' : ' sub-processors'))

  const hours = accepted
    .filter((t) => t.kind === 'BREACH_NOTICE' && t.code === 'WITHIN_HOURS')
    .map((t) => Number(heldValue(t)))
    .filter((h) => Number.isFinite(h))
  if (hours.length > 0) parts.push('breach notice ' + Math.min(...hours) + ' h')
  else if (accepted.some((t) => t.kind === 'BREACH_NOTICE' && t.code === 'IMMEDIATELY')) parts.push('breach notice immediately')

  if (parts.length > 0) return 'Contract: ' + parts.join('; ') + '.'
  const waiting = set.counts.needsDecision
  if (waiting > 0) return waiting + (waiting === 1 ? ' contract reading needs a decision' : ' contract readings need a decision')
  return null
}
